import { AssessmentPoint, CandidateRating } from '../../types';

export const DEFAULT_ASSESSMENT_CRITERIA = [
  { name: 'Communication Skills', category: 'Personal Presentation', applicableFor: 'both' as const },
  { name: 'Attitude', category: 'Personal Presentation', applicableFor: 'both' as const },
  { name: 'Rank-Specific Competence', category: 'Professional Knowledge', applicableFor: 'both' as const },
  { name: 'Safety Awareness', category: 'Professional Knowledge', applicableFor: 'candidates' as const },
  { name: 'ISM / MARPOL / SOLAS / MLC Awareness', category: 'Professional Knowledge', applicableFor: 'candidates' as const },
  { name: 'Familiarity with Safety Drills', category: 'Safety & Emergency Preparedness', applicableFor: 'candidates' as const },
  { name: 'Response to Emergency Situations', category: 'Safety & Emergency Preparedness', applicableFor: 'both' as const },
  { name: 'Team Cooperation', category: 'Leadership & Teamwork', applicableFor: 'both' as const },
  { name: 'Leadership for Officers', category: 'Leadership & Teamwork', applicableFor: 'both' as const },
  { name: 'English Language', category: 'Ability', applicableFor: 'both' as const },
];

export const DEGREE_LEVELS = [
  { value: 1, label: 'Unsatisfactory', range: '0-59%', color: 'bg-red-100 text-red-800 border-red-300', radioColor: 'text-red-600' },
  { value: 2, label: 'Average', range: '60-74%', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', radioColor: 'text-yellow-600' },
  { value: 3, label: 'Good', range: '75-89%', color: 'bg-blue-100 text-blue-800 border-blue-300', radioColor: 'text-blue-600' },
  { value: 4, label: 'Excellent', range: '90-100%', color: 'bg-green-100 text-green-800 border-green-300', radioColor: 'text-green-600' },
];

export interface CandidateEvaluation {
  id: string;
  candidateId: string;
  candidateName: string;
  jobTitle: string;
  evaluationDate: string;
  evaluatorName: string;
  totalScore: number;
  maxScore: number;
  percentage: number;
  status: 'excellent' | 'good' | 'average' | 'unsatisfactory';
  ratings: CandidateRating[];
  ratingMap?: { [pointId: string]: number };
  notes?: string;
}

export function getStatusFromPercentage(percentage: number): 'excellent' | 'good' | 'average' | 'unsatisfactory' {
  if (percentage >= 90) return 'excellent';
  if (percentage >= 75) return 'good';
  if (percentage >= 60) return 'average';
  return 'unsatisfactory';
}

export function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'excellent': return 'bg-green-100 text-green-800';
    case 'good': return 'bg-blue-100 text-blue-800';
    case 'average': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-red-100 text-red-800';
  }
}

export function getProgressBarClass(percentage: number): string {
  if (percentage >= 90) return 'bg-green-600';
  if (percentage >= 75) return 'bg-blue-600';
  if (percentage >= 60) return 'bg-yellow-600';
  return 'bg-red-600';
}

export function buildDefaultPoints(): AssessmentPoint[] {
  return DEFAULT_ASSESSMENT_CRITERIA.map((c, i) => ({
    id: `default-${i}`,
    documentId: undefined,
    name: c.name,
    description: '',
    category: c.category as any,
    weight: 4,
    isActive: true,
    applicableFor: c.applicableFor as any,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }));
}

export function groupByCategory(points: AssessmentPoint[]): { category: string; points: AssessmentPoint[] }[] {
  const map = new Map<string, AssessmentPoint[]>();
  for (const p of points) {
    const cat = p.category || 'Other';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat)!.push(p);
  }
  return Array.from(map.entries()).map(([category, pts]) => ({ category, points: pts }));
}

export function mapEvaluationForExport(e: CandidateEvaluation) {
  return {
    'Candidate Name': e.candidateName, 'Job Title': e.jobTitle,
    'Evaluation Date': new Date(e.evaluationDate).toLocaleDateString(),
    'Evaluator': e.evaluatorName, 'Score': `${e.percentage.toFixed(1)}%`,
    'Points': `${e.totalScore}/${e.maxScore}`,
    'Status': e.status.charAt(0).toUpperCase() + e.status.slice(1),
    'Notes': e.notes || 'N/A',
  };
}

export function mapPointForExport(p: AssessmentPoint) {
  return {
    'Point Name': p.name,
    'Category': p.category.charAt(0).toUpperCase() + p.category.slice(1),
    'Weight': p.weight,
    'Applicable For': p.applicableFor === 'both' ? 'Both' : p.applicableFor.charAt(0).toUpperCase() + p.applicableFor.slice(1),
    'Status': p.isActive ? 'Active' : 'Inactive',
    'Description': p.description,
    'Created Date': new Date(p.createdAt).toLocaleDateString(),
    'Updated Date': new Date(p.updatedAt).toLocaleDateString(),
  };
}

export function printAssessmentSheet(evaluation: CandidateEvaluation, criteria: AssessmentPoint[]) {
  const grouped = groupByCategory(criteria);

  const tableRows = grouped.map(group => {
    const categoryHeader = `<tr><td colspan="6" style="background:#e8edf3;font-weight:bold;padding:8px 10px;font-size:13px;">${group.category}</td></tr>`;
    const criteriaRows = group.points.map(point => {
      const score = evaluation.ratingMap?.[point.id] || 0;
      return `<tr>
        <td style="padding:6px 10px;border:1px solid #bbb;">${point.name}</td>
        <td style="text-align:center;border:1px solid #bbb;">${score === 1 ? '✓' : ''}</td>
        <td style="text-align:center;border:1px solid #bbb;">${score === 2 ? '✓' : ''}</td>
        <td style="text-align:center;border:1px solid #bbb;">${score === 3 ? '✓' : ''}</td>
        <td style="text-align:center;border:1px solid #bbb;">${score === 4 ? '✓' : ''}</td>
      </tr>`;
    }).join('');
    return categoryHeader + criteriaRows;
  }).join('');

  const html = `<!DOCTYPE html>
<html dir="ltr">
<head>
  <meta charset="UTF-8">
  <title>Assessment For Candidates</title>
  <style>
    @page { size: A4; margin: 1.5cm; }
    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #222; }
    .header { text-align: center; margin-bottom: 18px; }
    .header h2 { margin: 0; font-size: 16px; }
    .header .arabic { font-size: 15px; direction: rtl; }
    .header .title { font-size: 18px; font-weight: bold; margin: 12px 0 6px; text-decoration: underline; }
    .info-table { width: 100%; margin-bottom: 14px; border-collapse: collapse; }
    .info-table td { padding: 5px 8px; font-size: 13px; }
    .info-table .label { font-weight: bold; width: 120px; }
    .assessment-table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
    .assessment-table th { background: #2563eb; color: #fff; padding: 8px 10px; font-size: 12px; border: 1px solid #bbb; }
    .assessment-table td { padding: 6px 10px; font-size: 12px; border: 1px solid #bbb; }
    .legend { margin: 14px 0; font-size: 12px; }
    .legend table { border-collapse: collapse; }
    .legend td { padding: 4px 12px; border: 1px solid #bbb; font-size: 12px; }
    .notes-section { margin-top: 14px; }
    .notes-section .notes-box { border: 1px solid #bbb; min-height: 60px; padding: 8px; font-size: 12px; }
    .signatures { margin-top: 30px; display: flex; justify-content: space-between; }
    .sig-block { text-align: center; width: 40%; }
    .sig-line { border-top: 1px solid #333; margin-top: 40px; padding-top: 4px; font-size: 12px; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <div class="header">
    <h2>PetroMarine Offshore Projects Support Co.</h2>
    <h2 class="arabic">شركة بترومارين لدعم المشاريع البحرية</h2>
    <div class="title">Assessment For Candidates</div>
  </div>

  <table class="info-table">
    <tr>
      <td class="label">Candidate Name:</td>
      <td>${evaluation.candidateName}</td>
      <td class="label">Date:</td>
      <td>${new Date(evaluation.evaluationDate).toLocaleDateString()}</td>
    </tr>
    <tr>
      <td class="label">Job Title:</td>
      <td>${evaluation.jobTitle}</td>
      <td class="label">Evaluator:</td>
      <td>${evaluation.evaluatorName}</td>
    </tr>
  </table>

  <table class="assessment-table">
    <thead>
      <tr>
        <th style="text-align:left;width:40%;">Assessment Criteria</th>
        <th style="width:15%;">Unsatisfactory<br/><span style="font-weight:normal;font-size:10px;">0-59%</span></th>
        <th style="width:15%;">Average<br/><span style="font-weight:normal;font-size:10px;">60-74%</span></th>
        <th style="width:15%;">Good<br/><span style="font-weight:normal;font-size:10px;">75-89%</span></th>
        <th style="width:15%;">Excellent<br/><span style="font-weight:normal;font-size:10px;">90-100%</span></th>
      </tr>
    </thead>
    <tbody>
      ${tableRows}
    </tbody>
  </table>

  <div class="legend">
    <strong>Rating Scale:</strong>
    <table style="margin-top:4px;">
      <tr>
        <td><strong>Unsatisfactory</strong></td><td>0 – 59%</td>
        <td><strong>Average</strong></td><td>60 – 74%</td>
        <td><strong>Good</strong></td><td>75 – 89%</td>
        <td><strong>Excellent</strong></td><td>90 – 100%</td>
      </tr>
    </table>
  </div>

  <div class="notes-section">
    <strong>Notes:</strong>
    <div class="notes-box">${evaluation.notes || ''}</div>
  </div>

  <div class="signatures">
    <div class="sig-block">
      <div class="sig-line">Evaluator Signature</div>
    </div>
    <div class="sig-block">
      <div class="sig-line">HR Manager Signature</div>
    </div>
  </div>

  <script>window.onload = function() { window.print(); };</script>
</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
}
